import { afterNextRender, booleanAttribute, ChangeDetectionStrategy, Component, DestroyRef, inject, input, model, Renderer2, signal, TemplateRef, viewChild, ViewContainerRef } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'

import { MatIconModule } from '@angular/material/icon'
import { MatTooltipModule } from '@angular/material/tooltip'

import { Scroll } from '../scroll'

@Component({
	selector: 'app-horizontal-scroll-buttons',
	imports: [MatIconModule, MatTooltipModule],
	templateUrl: './horizontal-scroll-buttons.html',
	styleUrl: './horizontal-scroll-buttons.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HorizontalScrollButtons {
	// injections
	private readonly destroyRef = inject(DestroyRef)
	private readonly renderer = inject(Renderer2)
	private readonly scroll = inject(Scroll)
	private readonly viewContainerRef = inject(ViewContainerRef)
	// inputs: primary
	readonly scrollableElement = model<HTMLElement>()
	readonly hideButtons = input<boolean>(false, { alias: 'hide-buttons' })
	readonly scrollAmountPx = input(200, { alias: 'scroll-amount-px' }) // default value used topic buttons component
	// inputs: positioning & styling
	readonly noOpacity = input(false, { alias: 'no-opacity', transform: booleanAttribute })
	// queries
	private readonly leftButtonTemplate = viewChild.required<TemplateRef<HTMLElement>>('leftButton')
	private readonly rightButtonTemplate = viewChild.required<TemplateRef<HTMLElement>>('rightButton')
	// state
	protected readonly canScrollLeft = signal(false)
	protected readonly canScrollRight = signal(false)

	constructor() {
		// delay DOM manipulation until after hydration to avoid corrupting the SSR DOM structure
		afterNextRender(() => {
			const hostElement = this.viewContainerRef.element.nativeElement as HTMLElement

			// default to parent element if no scrollable or button container element provided
			if (!this.scrollableElement()) this.scrollableElement.set(hostElement.parentElement!)

			this.insertButtonsIntoContainer(hostElement)
			this.syncScrollButtonVisibilityWithHorizontalScrollState()
		})
	}

	/** Unwraps button templates from host element by moving their content to the parent element and removing the host element. */
	private insertButtonsIntoContainer(hostElement: HTMLElement): void {
		const container = this.scrollableElement()!
		const leftButtonView = this.viewContainerRef.createEmbeddedView(this.leftButtonTemplate())
		const rightButtonView = this.viewContainerRef.createEmbeddedView(this.rightButtonTemplate())

		// insert left button as first child and right button as last child
		leftButtonView.rootNodes.forEach(node => this.renderer.insertBefore(container, node, container.firstChild))
		rightButtonView.rootNodes.forEach(node => this.renderer.appendChild(container, node))

		// remove the host element now that its content has been moved to the other container
		hostElement.remove()
	}

	/** Keep scroll button visibility in sync with the scrollable element's horizontal scroll state. */
	private syncScrollButtonVisibilityWithHorizontalScrollState(): void {
		this.scroll.getHorizontalScrollability(this.scrollableElement()!).pipe(
			takeUntilDestroyed(this.destroyRef),
		).subscribe(({ canScrollLeft, canScrollRight }) => {
			this.canScrollLeft.set(canScrollLeft && !this.hideButtons())
			this.canScrollRight.set(canScrollRight && !this.hideButtons())
		})
	}

	/** Scrolls the scrollable element left or right by the amount specified in `this.scrollAmountPx`. */
	protected scrollHorizontally(direction: 'left' | 'right'): void {
		let scrollByPx = direction === 'left' ? -this.scrollAmountPx() : this.scrollAmountPx()
		this.scroll.scrollHorizontally(this.scrollableElement()!, scrollByPx)
	}

}
